import React, { useMemo, useState } from 'react';

const formatINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
};

const TemplatePreviewBill = ({
  draft,
  previewResult,
  isPrivileged,
  onRunPreview,
}) => {
  const inputFields = useMemo(
    () => (draft?.fields || [])
      .filter((f) => String(f.fieldType || 'INPUT').toUpperCase() === 'INPUT' && String(f.status || 'ACTIVE') === 'ACTIVE')
      .sort((a, b) => a.sequence - b.sequence),
    [draft],
  );

  const [values, setValues] = useState(() => {
    const init = {};
    inputFields.forEach((f) => { init[f.fieldCode] = f.fieldCode === 'GROSS_WORK_DONE' ? 10000000 : 0; });
    return init;
  });
  const [showJson, setShowJson] = useState(false);
  const [previousValues, setPreviousValues] = useState({});

  React.useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      inputFields.forEach((f) => {
        if (next[f.fieldCode] == null) next[f.fieldCode] = f.fieldCode === 'GROSS_WORK_DONE' ? 10000000 : 0;
      });
      return next;
    });
  }, [inputFields]);

  const resultMap = previewResult?.effectiveFieldValues || previewResult?.values || {};

  return (
    <div className="panel ra-template-preview">
      <h4>Test Calculation</h4>
      <p className="ra-tpl-help">Enter sample values like an RA Bill. No bill is created.</p>

      <div className="ra-tpl-preview-grid">
        <div>
          <h5>Test Values</h5>
          {inputFields.map((f) => (
            <div key={f.fieldCode} className="form-group">
              <label>{f.fieldLabel || f.fieldCode}</label>
              <input
                type="number"
                value={values[f.fieldCode] ?? ''}
                onChange={(e) => setValues((p) => ({ ...p, [f.fieldCode]: e.target.value === '' ? '' : Number(e.target.value) }))}
              />
            </div>
          ))}
          <button
            type="button"
            className="btn-primary"
            onClick={() => onRunPreview(values, previousValues)}
          >
            Calculate
          </button>
          {isPrivileged && (
            <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setShowJson((v) => !v)}>
              {showJson ? 'Hide' : 'View'} Test JSON
            </button>
          )}
          {showJson && (
            <pre className="ra-tpl-code-block">{JSON.stringify(values, null, 2)}</pre>
          )}
        </div>

        <div>
          <h5>Results</h5>
          {!previewResult ? (
            <p className="ra-tpl-muted">Run Calculate to see results.</p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Current</th>
                    <th>Previous</th>
                    <th>Since Previous</th>
                  </tr>
                </thead>
                <tbody>
                  {(draft.fields || [])
                    .filter((f) => String(f.status || 'ACTIVE') === 'ACTIVE')
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((f) => {
                      const current = resultMap[f.fieldCode];
                      const prev = previousValues[f.fieldCode] ?? 0;
                      const cfg = f.metadata?.businessConfig;
                      const showCmp = (cfg?.comparisonBehaviour || 'CURRENT_MINUS_PREVIOUS') === 'CURRENT_MINUS_PREVIOUS';
                      const since = showCmp && current != null ? Number(current) - Number(prev || 0) : null;
                      return (
                        <tr key={f.fieldCode}>
                          <td>{f.fieldLabel || f.fieldCode}</td>
                          <td className="amount-cell">{formatINR(current)}</td>
                          <td className="amount-cell">
                            <input
                              type="number"
                              className="ra-tpl-mini-input"
                              value={previousValues[f.fieldCode] ?? ''}
                              onChange={(e) => setPreviousValues((p) => ({ ...p, [f.fieldCode]: e.target.value === '' ? '' : Number(e.target.value) }))}
                            />
                          </td>
                          <td className="amount-cell">{since == null ? '—' : formatINR(since)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
          {previewResult?.engine && (
            <p className="ra-tpl-help">Engine: {previewResult.engine}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewBill;
